import { z } from 'zod';

const calendar_week_totals = z
  .object({
    critical: z.number().int(),
    at_risk: z.number().int(),
    filled: z.number().int(),
    passing_on_pay: z.number().int().optional(),
    fill_reasons: z.record(z.number().int()).optional(),
  })
  .passthrough();
const shift_ribbon = z
  .object({
    type: z.enum([
      'needs_approval',
      'in_progress',
      'starts_in',
      'ended_unfilled',
    ]),
    hours: z.number().nullish(),
  })
  .passthrough();
const pay_surge_badge = z
  .object({
    status: z.enum(['active', 'paused']),
    applied_cents: z.number().int(),
  })
  .passthrough();
const shift_backups = z
  .object({
    enabled: z.boolean().nullish(),
    off_level: z.enum(['job', 'order', 'client']).nullish(),
    on_standby: z.number().int(),
    invited: z.number().int(),
  })
  .passthrough();
const fill_reason = z
  .object({
    code: z.string(),
    label: z.string(),
    short_label: z.string(),
    on_card: z.boolean(),
  })
  .passthrough();
const fill_diagnosis = fill_reason.and(
  z.object({ others: z.array(fill_reason) }).passthrough(),
);
const fill_next_step = z
  .object({
    type: z.string(),
    label: z.string(),
    at: z.string().datetime({ offset: true }).nullish(),
  })
  .passthrough();
const fill_pool = z
  .object({
    round: z.number().int(),
    rounds_total: z.number().int(),
    next_at: z.string().datetime({ offset: true }).nullish(),
    sent_early: z.boolean(),
    label: z.string(),
    note: z.string().nullish(),
  })
  .passthrough();
const fill_signals = z
  .object({
    diagnosis: fill_diagnosis,
    too_early: z.boolean(),
    next_step: fill_next_step.nullish(),
    pool: fill_pool.nullish(),
    expected_to_show: z.number().nullish(),
    likely_to_fill_pct: z.number().int().nullish(),
  })
  .passthrough();
const shift_card = z
  .object({
    id: z.number().int(),
    job_id: z.number().int(),
    order_id: z.number().int().nullish(),
    order_uuid: z.string().nullish(),
    client_id: z.number().int().nullish(),
    client_name: z.string().nullish(),
    venue_name: z.string().nullish(),
    billing_account_id: z.number().int().nullish(),
    billing_account_name: z.string().nullish(),
    skill_name: z.string().nullish(),
    location_code: z.string().nullish(),
    address_line1: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    zip: z.string().nullish(),
    timezone: z.string().nullish(),
    start_at: z.string().nullish(),
    end_at: z.string().nullish(),
    hired_count: z.number().int(),
    no_show_count: z.number().int().optional(),
    quantity: z.number().int(),
    fill_status: z.enum(['filled', 'at_risk', 'critical']).nullable(),
    job_type: z.string().nullish(),
    restricted_pool: z.boolean(),
    dependability_drop: z.boolean(),
    ribbon: shift_ribbon.nullish(),
    pay_surge: pay_surge_badge.nullish(),
    backups: shift_backups.nullish(),
    fill_signals: fill_signals.nullish(),
  })
  .passthrough();
const calendar_day = z
  .object({
    date: z.string(),
    weekday: z.string(),
    is_today: z.boolean(),
    fill_rate_pct: z.number().int().nullish(),
    shift_count: z.number().int(),
    shifts: z.array(shift_card),
  })
  .passthrough();
const calendar_week_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        start_date: z.string(),
        end_date: z.string(),
        time_zone: z.string(),
        totals: calendar_week_totals,
        days: z.array(calendar_day),
      })
      .passthrough(),
  })
  .passthrough();
const error_response = z
  .object({ success: z.literal(false), errors: z.array(z.string()) })
  .passthrough();
const summary_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        no_show: z.number().int(),
        missing_timesheet: z.number().int(),
        needs_approval: z.number().int(),
      })
      .passthrough(),
  })
  .passthrough();
const pagination = z
  .object({
    total: z.number().int(),
    page: z.number().int(),
    per_page: z.number().int(),
    total_pages: z.number().int(),
  })
  .passthrough();
const no_show_worker = z
  .object({
    id: z.number().int(),
    worker_id: z.number().int(),
    name: z.string().nullish(),
    avatar_url: z.string().nullish(),
    phone: z.string().nullish(),
    status: z.enum([
      'no_show',
      'awaiting_confirmation',
      'backfill_requested',
      'escalated',
    ]),
    confirmed: z.boolean(),
    marked_automatically: z.boolean(),
    marked_by: z.string().nullish(),
    marked_at: z.string().nullish(),
  })
  .passthrough();
const no_show_shift = z
  .object({
    shift_id: z.number().int(),
    job_id: z.number().int(),
    order_id: z.number().int().nullish(),
    client_name: z.string().nullish(),
    skill_name: z.string().nullish(),
    location_code: z.string().nullish(),
    start_at: z.string().nullish(),
    end_at: z.string().nullish(),
    timezone: z.string().nullish(),
    workers: z.array(no_show_worker),
  })
  .passthrough();
const no_show_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        total: z.number().int(),
        pagination: pagination,
        confirmed_total: z.number().int().optional(),
        unresolved_total: z.number().int().optional(),
        shifts: z.array(no_show_shift),
      })
      .passthrough(),
  })
  .passthrough();
const missing_timesheet_worker = z
  .object({
    id: z.number().int(),
    timesheet_id: z.number().int().nullish(),
    job_worker_id: z.number().int().nullish(),
    worker_id: z.number().int(),
    name: z.string().nullish(),
    avatar_url: z.string().nullish(),
    phone: z.string().nullish(),
    status: z.literal('missing_timesheet'),
    check_in: z.string().nullish(),
    check_out: z.string().nullish(),
    break_minutes: z.number().int().nullish(),
    total_minutes: z.number().int().nullish(),
    average_rating: z.number().nullish(),
    jobs_rating_count: z.number().int().nullish(),
  })
  .passthrough();
const missing_timesheet_shift = z
  .object({
    shift_id: z.number().int(),
    job_id: z.number().int(),
    order_id: z.number().int().nullish(),
    client_name: z.string().nullish(),
    skill_name: z.string().nullish(),
    location_code: z.string().nullish(),
    start_at: z.string().nullish(),
    end_at: z.string().nullish(),
    timezone: z.string().nullish(),
    workers: z.array(missing_timesheet_worker),
  })
  .passthrough();
const missing_timesheet_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        total: z.number().int(),
        pagination: pagination,
        shifts: z.array(missing_timesheet_shift),
      })
      .passthrough(),
  })
  .passthrough();
const needs_approval_check_event = z
  .object({
    at: z.string().nullish(),
    verification_method: z.string().nullish(),
    distance_feet: z.number().int().nullish(),
    distance_miles: z.number().nullish(),
    has_coordinates: z.boolean().nullish(),
    flagged: z.boolean(),
  })
  .passthrough();
const needs_approval_worker = z
  .object({
    timesheet_id: z.number().int(),
    worker_id: z.number().int().nullish(),
    worker_name: z.string().nullish(),
    avatar_url: z.string().nullish(),
    checkin: needs_approval_check_event,
    checkout: needs_approval_check_event,
    total_hours: z.number().nullish(),
  })
  .passthrough();
const needs_approval_shift = z
  .object({
    shift_id: z.number().int(),
    job_id: z.number().int(),
    order_id: z.number().int().nullish(),
    client_name: z.string().nullish(),
    skill_name: z.string().nullish(),
    location_code: z.string().nullish(),
    start_at: z.string().nullish(),
    end_at: z.string().nullish(),
    timezone: z.string().nullish(),
    hired_count: z.number().int(),
    quantity: z.number().int(),
    status: z.enum(['needs_approval', 'needs_review']).nullish(),
    status_subtext: z.string().nullish(),
    location_flag_count: z.number().int().nullish(),
    workers: z.array(needs_approval_worker).nullish(),
  })
  .passthrough();
const needs_approval_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        total: z.number().int(),
        pagination: pagination,
        shifts: z.array(needs_approval_shift),
      })
      .passthrough(),
  })
  .passthrough();
const shift_detail_supervisor = z
  .object({ name: z.string().nullable(), phone: z.string().nullable() })
  .partial()
  .passthrough();
const shift_detail_last_message = z
  .object({
    kind: z.string(),
    label: z.string(),
    channel: z.string().nullable(),
    status: z.enum(['sent', 'held']),
    sent_at: z.string().nullable(),
    scheduled_for: z.string().nullable(),
  })
  .passthrough();
const shift_detail_worker = z
  .object({
    worker_id: z.number().int().nullish(),
    name: z.string().nullish(),
    avatar_url: z.string().nullish(),
    dependability_score: z.number().int(),
    below_gate: z.boolean(),
    gate_reason: z
      .enum([
        'eligible',
        'insufficient_shifts',
        'active_violation',
        'lta_exempt',
        'favorite_exempt',
      ])
      .nullish(),
    tier: z.enum(['below', 'qualifying', 'unevaluated']),
    status: z.string().nullish(),
    status_label: z.string().nullish(),
    hired: z.boolean(),
    dropped: z.boolean().optional(),
    dropped_at: z.string().nullish(),
    removed: z.boolean().optional(),
    removed_at: z.string().nullish(),
    no_show: z.boolean().optional(),
    no_show_at: z.string().nullish(),
    last_message: shift_detail_last_message.nullish(),
  })
  .passthrough();
const shift_detail_invite = z
  .object({
    worker_id: z.number().int().nullish(),
    name: z.string().nullish(),
    avatar_url: z.string().nullish(),
    status: z.string(),
    status_label: z.string(),
    invited_at: z.string().nullish(),
    declined_at: z.string().nullish(),
    expired_at: z.string().nullish(),
    decline_reason: z.string().nullish(),
    decline_reason_label: z.string().nullish(),
    decline_note: z.string().nullish(),
    dependability_score: z.number().int().nullish(),
    below_gate: z.boolean().nullish(),
    tier: z.string().nullish(),
    hire_eligible: z.boolean().optional(),
    hire_blocked_reason: z.string().nullish(),
    hire_blocked_label: z.string().nullish(),
  })
  .passthrough();
const shift_handling = z
  .object({
    state: z.enum(['unclaimed', 'mine', 'other']),
    claimable: z.boolean(),
    holder_name: z.string().nullish(),
    claimed_at: z.string().nullish(),
    nudge_at: z.string().nullish(),
    expires_at: z.string().nullish(),
  })
  .passthrough();
const fill_signals_detail = fill_signals.and(
  z
    .object({
      surge_step: fill_next_step.nullish(),
      evidence: z
        .object({
          invited: z.number().int(),
          viewed: z.number().int(),
          declined: z.number().int(),
          accepted: z.number().int(),
          pending_requests: z.number().int(),
          top_decline_reasons: z.array(
            z
              .object({
                code: z.string(),
                label: z.string(),
                count: z.number().int(),
              })
              .passthrough(),
          ),
          market_median_cents: z.number().int().nullish(),
          pay_cents: z.number().int().nullish(),
        })
        .passthrough(),
      computed_at: z.string().datetime({ offset: true }).nullish(),
    })
    .passthrough(),
);
const shift_detail_activity = z
  .object({
    id: z.number().int(),
    event_type: z.string(),
    title: z.string().nullish(),
    avatar_url: z.string().nullish(),
    worker_name: z.string().nullish(),
    occurred_at: z.string().nullish(),
    actor_name: z.string().nullish(),
    actor_full_name: z.string().nullish(),
    actor_avatar_url: z.string().nullish(),
    automated: z.boolean().optional(),
    location_flagged: z.boolean().nullish(),
    detail: z.string().nullish(),
    segments: z
      .array(
        z
          .object({
            type: z.enum([
              'actor',
              'text',
              'worker',
              'workers',
              'strong',
              'quote',
            ]),
            text: z.string(),
            worker_id: z.number().int().nullish(),
            worker_ids: z.array(z.number().int()).nullish(),
            names: z.array(z.string()).nullish(),
          })
          .passthrough(),
      )
      .nullish(),
  })
  .passthrough();
const shift_detail_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        job_id: z.number().int(),
        order_id: z.number().int().nullish(),
        client_id: z.number().int().nullish(),
        client_name: z.string().nullish(),
        venue_name: z.string().nullish(),
        billing_account_id: z.number().int().nullish(),
        billing_account_name: z.string().nullish(),
        logo_url: z.string().nullish(),
        status: z.string().nullish(),
        status_label: z.string().nullish(),
        fill_status: z.enum(['filled', 'at_risk', 'critical']).nullish(),
        restricted_pool: z.boolean().optional(),
        job_type: z.string().nullish(),
        bg_check_required: z.boolean().optional(),
        dependability_gate: z
          .enum(['strict', 'relaxed', 'off', 'unknown'])
          .optional(),
        dependability_gate_label: z.string().optional(),
        dependability_gate_enabled: z.boolean().optional(),
        dependability_gate_reason: z.string().nullish(),
        qr_enabled: z.boolean().optional(),
        worker_classification: z.string().nullish(),
        role: z.string().nullish(),
        start_at: z.string().nullish(),
        end_at: z.string().nullish(),
        timezone: z.string().nullish(),
        duration_hours: z.number().nullish(),
        address_line1: z.string().nullish(),
        city: z.string().nullish(),
        state: z.string().nullish(),
        zip: z.string().nullish(),
        supervisor: shift_detail_supervisor.nullish(),
        hired_count: z.number().int(),
        no_show_count: z.number().int().optional(),
        quantity: z.number().int(),
        percent_filled: z.number().int().optional(),
        gate_threshold: z.number().int().nullish(),
        workers: z.array(shift_detail_worker),
        invites: z.array(shift_detail_invite).optional(),
        handling: shift_handling.nullish(),
        pay_surge: pay_surge_badge.nullish(),
        fill_signals: fill_signals_detail.nullish(),
        fill_signals_held: z.boolean().optional(),
        recent_activity: z.array(shift_detail_activity).optional(),
        has_activity: z.boolean().optional(),
      })
      .passthrough(),
  })
  .passthrough();
const shift_activity_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        activities: z.array(shift_detail_activity),
        pagination: pagination,
      })
      .passthrough(),
  })
  .passthrough();
const location_flag_point = z
  .object({ lat: z.number(), lng: z.number() })
  .passthrough();
const review_check_event = z
  .object({
    at: z.string().nullish(),
    verification_method: z.string().nullish(),
    distance_feet: z.number().int().nullish(),
    distance_miles: z.number().nullish(),
    flagged: z.boolean(),
    location_flag: z
      .object({
        id: z.number().int(),
        side: z.string(),
        headline: z.string(),
        distance_label: z.string(),
        radius_feet: z.number().int(),
        venue_name: z.string().nullish(),
        venue_address: z.string(),
        anchor: location_flag_point.nullish(),
        flagged_point: location_flag_point.nullish(),
      })
      .passthrough()
      .nullish(),
  })
  .passthrough();
const timesheet_review_response = z
  .object({
    success: z.literal(true),
    message: z.string().nullish(),
    data: z
      .object({
        timesheet_id: z.number().int(),
        job_id: z.number().int().nullish(),
        shift_id: z.number().int().nullish(),
        tag: z.string().nullish(),
        worker: z
          .object({
            id: z.number().int(),
            name: z.string().nullish(),
            avatar_url: z.string().nullish(),
          })
          .passthrough()
          .nullish(),
        similar: z
          .object({
            window_days: z.number().int().nullish(),
            total_count: z.number().int(),
            unresolved_count: z.number().int(),
          })
          .passthrough(),
        checkin: review_check_event,
        checkout: review_check_event,
        break_minutes: z.number().int(),
        net_hours: z.number().nullish(),
      })
      .passthrough(),
  })
  .passthrough();
const shift_handling_response = z
  .object({
    success: z.literal(true),
    message: z.string().nullish(),
    data: z
      .object({
        shift_id: z.number().int(),
        job_id: z.number().int(),
        handling: shift_handling,
      })
      .passthrough(),
  })
  .passthrough();
const dashboard_alert = z
  .object({
    id: z.number().int(),
    event_type: z.string(),
    shift_id: z.number().int(),
    job_id: z.number().int(),
    title: z.string(),
    message: z.string(),
    starts_at: z.string().nullish(),
    occurred_at: z.string(),
  })
  .passthrough();
const dashboard_handling_deadline = z
  .object({
    shift_id: z.number().int(),
    job_id: z.number().int(),
    starts_at: z.string().nullish(),
    nudge_at: z.string(),
    expires_at: z.string(),
  })
  .passthrough();
const dashboard_alerts_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        cursor: z.number().int(),
        alerts: z.array(dashboard_alert),
        my_handlings: z.array(dashboard_handling_deadline),
      })
      .passthrough(),
  })
  .passthrough();
const id_name_option = z
  .object({ id: z.number().int(), name: z.string().nullable() })
  .passthrough();
const value_label_option = z
  .object({ value: z.string(), label: z.string() })
  .passthrough();
const filters_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        clients: z.array(id_name_option),
        accounts: z.array(id_name_option),
        skills: z.array(id_name_option),
        regions: z.array(id_name_option),
        statuses: z.array(value_label_option),
        fill_rate_bands: z.array(value_label_option),
        shift_types: z.array(value_label_option),
      })
      .passthrough(),
  })
  .passthrough();
const previewPayEscalationPosting_Body = z
  .object({
    jobs: z.array(
      z
        .object({
          key: z.unknown().optional(),
          first_shift_start_at: z.string().datetime({ offset: true }),
          visibility: z.enum(['PUBLIC', 'PRIVATE']),
          publish: z.boolean().optional(),
          quantity: z.number().int().optional(),
          hire_count: z.number().int().optional(),
        })
        .passthrough(),
    ),
  })
  .partial()
  .passthrough();
const pay_escalation_posting_preview_job = z
  .object({
    key: z.unknown().nullable(),
    first_shift_start_at: z.string().datetime({ offset: true }),
    posts_at: z.string().datetime({ offset: true }),
    posts_now: z.boolean(),
    lead_time_minutes: z.number().int(),
    rate_cents: z.number().int(),
  })
  .passthrough();
const pay_escalation_posting_preview = z
  .object({
    customer_id: z.number().int(),
    enabled: z.boolean(),
    automation_on: z.boolean(),
    jobs: z.array(pay_escalation_posting_preview_job),
  })
  .passthrough();
const pay_escalation_posting_preview_response = z
  .object({ success: z.literal(true), data: pay_escalation_posting_preview })
  .passthrough();
const job_pay_escalation_tier = z
  .object({
    lead_time_minutes: z.number().int(),
    rate_cents: z.number().int(),
    fires_at: z.string().datetime({ offset: true }).nullish(),
    state: z.enum(['pending', 'fired', 'skipped']),
    fired_at: z.string().datetime({ offset: true }).nullish(),
  })
  .passthrough();
const job_pay_escalation = z
  .object({
    job_id: z.number().int(),
    status: z.enum(['off', 'upcoming', 'active', 'paused', 'ended']),
    enabled: z.boolean(),
    paused_at: z.string().datetime({ offset: true }).nullish(),
    ended_at: z.string().datetime({ offset: true }).nullish(),
    applied_cents: z.number().int(),
    base_pay_rate: z.number().int(),
    pay_rate: z.number().int(),
    first_shift_start_at: z.string().datetime({ offset: true }).nullish(),
    will_apply: z.boolean(),
    automation_on: z.boolean(),
    shifts_count: z.number().int(),
    is_holiday: z.boolean(),
    next_tier: job_pay_escalation_tier.nullish(),
    due_tier: job_pay_escalation_tier.nullish(),
    post_now_tier: job_pay_escalation_tier.nullish(),
    tiers: z.array(job_pay_escalation_tier),
    max_cents: z.number().int(),
    lead_time_options_minutes: z.array(z.number().int()),
  })
  .passthrough();
const job_pay_escalation_response = z
  .object({ success: z.literal(true), data: job_pay_escalation })
  .passthrough();

export const schemas = {
  calendar_week_totals,
  shift_ribbon,
  pay_surge_badge,
  shift_backups,
  fill_reason,
  fill_diagnosis,
  fill_next_step,
  fill_pool,
  fill_signals,
  shift_card,
  calendar_day,
  calendar_week_response,
  error_response,
  summary_response,
  pagination,
  no_show_worker,
  no_show_shift,
  no_show_response,
  missing_timesheet_worker,
  missing_timesheet_shift,
  missing_timesheet_response,
  needs_approval_check_event,
  needs_approval_worker,
  needs_approval_shift,
  needs_approval_response,
  shift_detail_supervisor,
  shift_detail_last_message,
  shift_detail_worker,
  shift_detail_invite,
  shift_handling,
  fill_signals_detail,
  shift_detail_activity,
  shift_detail_response,
  shift_activity_response,
  location_flag_point,
  review_check_event,
  timesheet_review_response,
  shift_handling_response,
  dashboard_alert,
  dashboard_handling_deadline,
  dashboard_alerts_response,
  id_name_option,
  value_label_option,
  filters_response,
  previewPayEscalationPosting_Body,
  pay_escalation_posting_preview_job,
  pay_escalation_posting_preview,
  pay_escalation_posting_preview_response,
  job_pay_escalation_tier,
  job_pay_escalation,
  job_pay_escalation_response,
};
